import {css} from 'aphrodite';
import React, {useEffect, useMemo, useState} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {SINGLE_DATASET_OUTPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {
  computeUpdatedTablesState,
  ConfigureCombineMethodContent,
  PairedTableSelector,
  useUnionColumnState,
} from 'analysis-tool/right-sidebar/combine-data-frames/CombineTablesFormComponents';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepConfigurationViewTemplate from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {assertAndContinue} from 'Asserts';
import {DataFrame, DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {COMBINE_TABLES_FINISHED_EVENT, COMBINE_TABLES_STARTED_EVENT} from 'performance/processAnalysesEvents';
import CombineTablesMethodSelector from 'pipelines/joins/CombineTablesMethodSelector';
import {getPreviewOutputStepData, PreviewOutput, PreviewStepParams} from 'pipelines/network/PipelineDAO';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {
  AsyncState,
  AsyncStatus,
  getMostRecentData,
  NOT_FETCHED_ASYNC_STATE,
} from 'ui-library/hooks/useAsyncState';
import {StepperV2, stepperStyles} from 'ui-library/stepper/Stepper';

import ChooseDifferentDataFrameCreationMethodButton from '../ChooseDifferentDataFrameCreationMethodButton';
import {getColumnMappingStepperStepParams} from '../column-mapping/ColumnMappingStepperStep';
import {
  getTemplateDataFrameColumns,
  selectedColumnsToColumnMappings,
} from '../column-mapping/ColumnMappingUtil';
import {getSharedDataFrameCreationMethodProps, openSelectedItemForNewStep} from '../DataFrameCreationUtils';
import {useAutoUpdatePreview} from '../hooks/useAutoUpdatePreview';
import DataFrameNameField from '../steps/DataFrameNameField';
import CombineDataFramesPreview from './CombineDataFramesPreview';
import {
  combineDataFramesGlobalState,
  DEFAULT_COMBINE_DATA_FRAMES_STATE,
  getCombineTablesStepParams,
} from './CombineDataFramesState';

const previewAsyncGlobalState =
  makeUseGlobalClientState<
    AsyncState<
      PreviewOutput<PipelineStepType.JOIN>,
      [PreviewStepParams<PipelineStepType.JOIN> | PreviewStepParams<PipelineStepType.UNION_DATASETS>]
    >
  >(NOT_FETCHED_ASYNC_STATE);

const isFinalizingGlobalState = makeUseGlobalClientState<boolean>(false);

/**
 * @package
 */
export type CombineDataFramesViewProps = Pick<
  MainViewComponentProps<AnalysisMainViewItemType.COMBINE_DATA_FRAMES>,
  | 'analysis'
  | 'clientStateId'
  | 'onCloseStepInitializationView'
  | 'onOpenStepInitializationView'
  | 'selectedItem'
>;

export default function CombineDataFramesView({
  analysis,
  clientStateId,
  onCloseStepInitializationView,
  onOpenStepInitializationView,
  selectedItem,
}: CombineDataFramesViewProps): JSX.Element {
  const [state, setState] = combineDataFramesGlobalState.useGlobalClientState(clientStateId);
  const [previewState, setPreviewState] = previewAsyncGlobalState.useGlobalClientState(clientStateId);
  const [isFinalizing, setIsFinalizing] = isFinalizingGlobalState.useGlobalClientState(clientStateId);
  const unionColumnState = useUnionColumnState();
  const [selectedColumns, setSelectedColumns] = useState<Array<DataFrameColumnDef | null>>([]);
  const [isManuallyChanged, setIsManuallyChanged] = useState(false);

  useEffect(() => {
    logProfilerTimingEvent(COMBINE_TABLES_STARTED_EVENT);
  }, []);

  function generatePreview(
    data: PreviewStepParams<PipelineStepType.JOIN>,
    setAsyncState: (nextState: AsyncState<PreviewOutput<PipelineStepType.JOIN>>) => void
  ): void {
    AnalysisActions.runPreviewAnalysisStep(data, {status: AsyncStatus.NOT_FETCHED}, setAsyncState);
  }

  const {previewStepParams, addStepParams} = getCombineTablesStepParams(
    state.combineMethod,
    state.joinType,
    state.leftTable,
    state.rightTable,
    state.relationships,
    unionColumnState.selectedPrimaryColumns,
    unionColumnState.selectedSecondaryColumns,
    state.dataFrameName,
    analysis.steps
  );

  useAutoUpdatePreview(previewStepParams, generatePreview, (nextState) => {
    setPreviewState(nextState);
  });

  const templateDataFrameColumns = useMemo(
    () => getTemplateDataFrameColumns(analysis, selectedItem.stepIdToReplace),
    [analysis, selectedItem.stepIdToReplace]
  );

  const mostRecentData = getMostRecentData(previewState);
  const previewDataFrame: DataFrame | null =
    (mostRecentData &&
      getPreviewOutputStepData(
        mostRecentData,
        SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
        PipelineStepDataKind.DATASET
      )[0]) ??
    null;

  const finalize = async (): Promise<void> => {
    try {
      if (addStepParams == null) {
        assertAndContinue(false, 'Tried to finalize combining data frames with incomplete form state.');
        return;
      }

      setIsFinalizing(true);
      const newStep = await AnalysisActions.addOrReplaceAndRunAnalysisStep(
        analysis.id,
        addStepParams,
        selectedItem.stepIdToReplace
          ? {
              originalStepId: selectedItem.stepIdToReplace,
              columnMapping: selectedColumnsToColumnMappings(selectedColumns, templateDataFrameColumns),
            }
          : undefined
      );
      logProfilerTimingEvent(COMBINE_TABLES_FINISHED_EVENT, {
        combinationType: 'join',
        joinType: state.joinType,
        error: null,
      });
      openSelectedItemForNewStep(newStep, onCloseStepInitializationView);
    } catch (e) {
      logProfilerTimingEvent(COMBINE_TABLES_FINISHED_EVENT, {
        combinationType: 'join',
        joinType: state.joinType,
        error: e,
      });
      throw e;
    } finally {
      setIsFinalizing(false);
      setState(DEFAULT_COMBINE_DATA_FRAMES_STATE);
    }
  };

  return (
    <StepConfigurationViewTemplate
      {...getSharedDataFrameCreationMethodProps(selectedItem)}
      clientStateId={`${clientStateId}-combine-data-frames-view`}
      dataFrames={[]}
      renderAdditionalPreviewContent={() => (
        <CombineDataFramesPreview
          clientStateId={clientStateId}
          state={state}
          steps={analysis.steps}
          previewState={previewState}
        />
      )}
      disableFinalizeButton={
        addStepParams == null ||
        isFinalizing ||
        (state.combineMethod === PipelineStepType.UNION_DATASETS && unionColumnState.hasDuplicateColumns)
      }
      isFinalizing={isFinalizing}
      messages={[]}
      onCancel={() => {
        setState(DEFAULT_COMBINE_DATA_FRAMES_STATE);
        onCloseStepInitializationView();
      }}
      onFinalize={finalize}
      title="Combine analysis tables"
      toolbar={
        <ChooseDifferentDataFrameCreationMethodButton
          onOpenStepInitializationView={(newSelectedItem: {
            type: AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD;
          }) => {
            onOpenStepInitializationView({...newSelectedItem, stepIdToReplace: selectedItem.stepIdToReplace});
          }}
        />
      }
    >
      <StepperV2
        steps={[
          {
            title: 'Select a combination method',
            content: (
              <CombineTablesMethodSelector
                combineTablesMethod={state.combineMethod}
                onSelectCombineTablesMethod={(combineMethod) => {
                  setState({...state, combineMethod});
                }}
              />
            ),
            key: 'select-combine-method',
          },
          {
            title: 'Select tables to combine',
            content: (
              <PairedTableSelector
                analysis={analysis}
                state={state}
                onUpdateTables={(leftTable, rightTable) => {
                  setState(computeUpdatedTablesState(state, leftTable, rightTable, analysis.steps));
                }}
                dataTestComponent="CombineDataFramesView"
              />
            ),
            key: 'select-tables',
          },
          {
            title: 'Configure combine method',
            content: (
              <ConfigureCombineMethodContent
                state={state}
                steps={analysis.steps}
                onUpdateJoinType={(joinType) => {
                  setState({...state, joinType});
                }}
                onUpdateRelationships={(relationships) => {
                  setState({...state, relationships});
                }}
                unionColumnState={unionColumnState}
              />
            ),
            key: 'configure-combine-method',
          },
          selectedItem.stepIdToReplace != null &&
            getColumnMappingStepperStepParams({
              templateDataFrameColumns,
              newDataFrameColumns: previewDataFrame?.columns ?? null,
              newDataFrameState: previewState.status,
              selectedColumns,
              setSelectedColumns,
              isManuallyChanged,
              setIsManuallyChanged,
              key: 'column-mapping',
            }),
          {
            title: 'Name table',
            content: (
              <DataFrameNameField
                hasUserEdits={state.dataFrameNameHasUserEdits}
                name={state.dataFrameName}
                onNameChange={(dataFrameName) => {
                  setState({...state, dataFrameName, dataFrameNameHasUserEdits: true});
                }}
              />
            ),
            key: 'name-table',
          },
        ].map((step) => {
          if (step == null) {
            return null;
          }
          if (step === false) {
            return false;
          }
          return {
            ...step,
            title: <div className={css(stepperStyles.defaultStepTitle)}>{step.title}</div>,
            content: <div className={css(stepperStyles.defaultStepContent)}>{step.content}</div>,
          };
        })}
      />
    </StepConfigurationViewTemplate>
  );
}
